import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { isStaffEmail } from "@utils/user_utils";
import { TrainingAPI } from "../../api/TrainingAPI";
import {useDispatch, useSelector} from "react-redux";
import { GameTable } from "./GameTable";

import "bootstrap/dist/css/bootstrap.min.css";
import { Navbar } from "../Navigation/Navbar";
import { Row, Col, Menu, Typography } from "antd";
import { useDataAccess, useUserPermissions } from "@components/PermissionedComponents";
import { DATA_STREAMS } from "@utils/data_access";

import "../../Styles/styles-PostData.css";
import "../../Styles/styles-Settings.css";
import { RestDays } from "./RestDays";
import { DrillMergeTable } from "./DrillMergeTable";
import { NicknameTable } from "./NicknameTable";
import { OverrideDatapoint } from "./OverrideDatapoint";
import { DeleteData } from "./DeleteData";
import { SessionManagement } from "./SessionManagement";
import { DailyActiveRoster } from "./DailyActiveRoster";
import { TbDatabaseCog } from "react-icons/tb";
import { PlayerOrdering } from "./PlayerOrdering";
import { OverviewSettings } from "./OverviewSettings/OverviewSettings";
import { CustomColorStrategies } from "./CustomColorStrategies/CustomColorStrategies";
import { PlatformPermissions } from "./PlatformPermissions/PlatformPermissions";
import { MetricsSettings } from "./MetricsSettings";
import { WellnessSurvey } from "./WellnessSurvey";
import { PlayerGrouping } from "./PlayerGrouping";
import { PlayerOnboarding } from "./PlayerOnboarding/PlayerOnboarding";
import { useAuth0 } from "@auth0/auth0-react";
import { SettingTitle } from "./SettingTitle";
import { TeamOutlined, UserOutlined, HighlightOutlined, LockOutlined, ApiOutlined, CalendarOutlined, LineChartOutlined } from "@ant-design/icons";
import { GiWhistle } from "react-icons/gi";
import { IoMdAirplane } from "react-icons/io";
import {FaChalkboardTeacher, FaRegAddressCard} from "react-icons/fa";
import { ImList2 } from "react-icons/im";
import { BsClipboard2Data } from "react-icons/bs";
import { BsDownload } from "react-icons/bs";
import {
  MdDriveFileRenameOutline,
  MdOutlinePersonalInjury,
  MdOutlineSettings,
  MdOutlineLibraryBooks,
  MdDeleteOutline,
  MdOutlineCalculate
  } from "react-icons/md";
  import { BsFlag } from "react-icons/bs";
import { IoBarChart } from "react-icons/io5";
import { PiListNumbers } from "react-icons/pi";
import { HiOutlineRectangleGroup } from "react-icons/hi2";
import { RiSurveyLine } from "react-icons/ri";
import { GrTrigger } from "react-icons/gr";
import { CgColorPicker } from "react-icons/cg";
import { TeamBaseline } from "./TeamBaseline";
import { AutomaticActions } from "./AutomaticActions/AutomaticActions.tsx";
import { ReportBulletPicker } from "./ReportBulletPicker/ReportBulletPicker";
import {CoachOnboarding} from "./CoachOnboarding";
import { ThirdPartyIntegration } from './ThirdPartyIntegration';
import { HelpButton } from "../Common/HelpButton";
import { TfiLock } from "react-icons/tfi";
import { ActivityLog } from "./ActivityLog";
import { PlatformUsage } from "./PlatformUsage/PlatformUsage";
import GpsMetricMapping from "./GpsMetricMapping";
import { setTeamMetrics, getTeamInfo } from "@store/actions/team.actions";
import FlagReminders from "./FlagReminders";
import { UserDefinedMetrics } from "./UserDefinedMetrics/UserDefinedMetrics";
import { GraphicsPackageDownloader } from "./GraphicPackageDownloader";
import { CustomCharts } from "./CustomCharts/CustomCharts";
import { UserDefinedFlags } from "./UserDefinedFlags/UserDefinedFlags";
import { MdOutlineFlag } from "react-icons/md";

const ROSTER_TEAMS = [
  "Demo Team",
  "Football Demo Team",
  "Montana WSOC",
  "Frenship HS Football",
  "Orlando Pride",
  "North Carolina Courage",
  "Racing Louisville WSOC",
  "Utah Warriors Rugby",
  "N2AthleteX",
  "Summerville Football",
  "Summerville Athletics",
  "Pittsburgh Steelers",
  "Temple University Football",
  "New York Rangers",
  "Las Vegas Raiders",
];

const ROSTER_ACCESS_ACCOUNTS = [
  "coachrubyn@berkeley.edu",
  "bnichols@mitty.com",
  "chapp103@d.umn.edu",
  "trevor.goldstein@msgsports.com",
  "antony.organ@msgsports.com",
  "coachrobinson@purdue.edu",
  "tulsastrength2025@gmail.com",
  "elyas.himmelgreen@tampabaysunfc.com",
  "sami.diamond@superleaguetampabay.com",
  "andrea.hayden@lynxbasketball.com",
  "judkinst@steelers.nfl.com",
  "gsmith@bayfc.com",
  "jdiaz@athletics.gatech.edu"
];

const SETTING_DESCRIPTIONS = {
  "overview": "Set up the basics for your team: your colors, ACWR thresholds, player app permissions and more.",
  "baselines-team": "Your baselines, your view. Set a value for every GPS metric by player or position, and the platform measures and colors your data against your own standards.",
  "activity-log": "A record of every action that your team makes on the platform.",
  "platform-usage": "See how your staff uses the platform: weekly active staff, the reports you generate, the data you capture and your most common actions.",
  "calendar-games": "Keep your schedule in one place. Games you add here appear on the calendar and unlock game-day analysis: how the days leading up to a game and the days after it compare.",
  "calendar-rest": "Give every day its context. Tags like travel, rest and lift days sit on the calendar next to your training data, so you can see why a day looked the way it did.",
  "players-roster": "Your team's roster in one place. An accurate roster means accurate data across the platform.",
  "players-nicknames": "Keep each player's data in one place. When a device or an upload uses a different name or a typo, a nickname links that data to the right player.",
  "players-ordering": "Set the default order of your roster. Every dropdown, table and report on the platform lists players in this order, so your staff finds players faster and sees the same order everywhere.",
  "players-groups": "Create groups that match how your staff works, such as positions, lines or rehab players. Each group becomes a filter across the platform and in reports, so you see the right players in one click.",
  "health-wellness": "Ask your athletes the questions that matter to you. Build a daily survey from our question bank, and see how your players feel before they train.",
  "override-datapoint": "Correct any value that came in from an integration, whether through an API or a CSV upload.",
  "delete-data": "Keep your dataset clean. Remove test sessions, duplicate imports or bad device data for any date range, so your trends and averages show only real sessions.",
  "session-management": "Your sessions, organized your way. Clear names and correct player assignments make every session report accurate and easy to read.",
  "daily-active-roster": "Your averages should show the players who trained. Set each player's active status by date, and team and position averages include only active players.",
  "automatic-actions": "Automate the routine. Scheduled imports, reminders and reports keep your data current and your athletes on track, with no manual steps.",
  "flag-reminders": "Close the loop on every flag. A reminder email brings flagged players back to your attention days later, so you can check that they recovered and catch problems before they grow.",
  "user-defined-metrics": "Track the numbers your staff actually uses. Combine any metrics with simple math, and the platform calculates the result for every player, every session, with no spreadsheets.",
  "user-defined-flags": "Spot the players who need attention before it becomes a problem. Write a rule once, such as a sharp drop in top speed or low sleep scores, and the platform flags every player who matches it.",
  "reports": "Make your GPS report say what matters to you. Pick the bullets from the bank, and choose the pages where each one shows.",
  "custom-coloring": "Color your data the way your staff reads it. Write your own if-then rules, define how each day's difficulty is colored, and stack rules in priority order, and every page follows them.",
  "coach-onboarding": "Bring your whole staff onto the platform. Create a login for each coach, and everyone works from the same data.",
  "platform-permissions": "Give every coach the access their role needs. Decide what each person can view and change, so sensitive settings and data stay in the right hands.",
};

// ponytail: temporary signpost after the move to the Health page; remove these two
// menu items and this component one release after the move.
const MovedToHealth = ({ page, path, verb = "has" }) => {
  const navigate = useNavigate();
  return (
    <Typography.Paragraph type="secondary" style={{ textAlign: "left" }}>
      {page} {verb} moved to the Health page.{" "}
      <Typography.Link onClick={() => navigate(path)} data-attr="settings.health-move.navigate">
        Go to {page} →
      </Typography.Link>
    </Typography.Paragraph>
  );
};

export const TeamSettings = (props) => {
  const trainingAPI = new TrainingAPI();

  const dispatch = useDispatch();

  const {
    info: {
      display_name: teamName,
      team_logo,
      bullet_picker_enabled,
      custom_colors_enabled,
      platform_permissions_enabled,
      third_party_integration_management_enabled,
      add_coach_enabled,
      gps_mapping_strategy_version,
      flag_reminder_enabled,
      user_defined_metrics_enabled,
      user_defined_flags_enabled,
    },
    players,
    allPlayers,
    playerGroups
  } = useSelector(({ team }) => team);
  const userPermissions = useUserPermissions();

  const { user } = useAuth0();
  const isStaff = isStaffEmail(user?.email);

  // Both pages gate each data type separately inside themselves, so these entries
  // only reflect access to the page. Override Datapoint keeps the feature-level
  // `OverrideDataPoints` key; Delete Data unlocks once the user can delete at
  // least one stream.
  const { canDelete } = useDataAccess();
  const canOverrideDatapoints = userPermissions?.OverrideDataPoints;
  const canDeleteAnyDatapoint = DATA_STREAMS.some(canDelete);

  const [possiblePositions, setPossiblePositions] = useState([]);
  const [sport, setSport] = useState(undefined);
  const [baselineOverviewData, setBaselineOverviewData] = useState([]);
  const [allMetrics, setAllMetrics] = useState([]);

  const { tab } = useParams();
  const navigate = useNavigate();

  const getBaselineOverviewData = () => {
    trainingAPI.getBaselineOverview().then((data) => {
      setBaselineOverviewData(data);
    });
  };

  const getPossiblePositions = () => {
    trainingAPI.getPositions(true).then((data) => {
      const posArr = data.positions ?? [];
      const isObjectArray = posArr.length > 0 && typeof posArr[0] === "object";
      setPossiblePositions(isObjectArray ? posArr.map((p) => p.position_name) : posArr);
      setSport(data.sport);
    });
  };

  const getAllMetrics = async () => {
    let metricsData = []

    try {
      metricsData = await trainingAPI.getTeamMetrics(false, false, false, false, false, null)
    } catch (err) {
      console.log("Error getting assignmetrics", err)
    }

    setAllMetrics(metricsData);
    dispatch(setTeamMetrics(metricsData))
    dispatch(getTeamInfo())
  };

  useEffect(() => {
    getBaselineOverviewData();
    getPossiblePositions();
    getAllMetrics();
  }, []);

  let contents = {
    "overview": (
      <OverviewSettings />
    ),
    "metrics": (
      <MetricsSettings
        getAllMetrics={getAllMetrics}
      />
    ),
    "activity-log": (
      <ActivityLog />
    ),
    "platform-usage": <PlatformUsage />,
    "baselines-team": (
      <TeamBaseline
        allMetrics={allMetrics}
        sport={sport}
        getBaselineOverviewData={getBaselineOverviewData}
      />
    ),
    "custom-charts":<CustomCharts/>,
    "calendar-games": <GameTable />,
    "calendar-rest": <RestDays />,
    "players-roster": <PlayerOnboarding userPermissions={userPermissions}/>,
    "players-nicknames": <NicknameTable players={allPlayers} />,
    "players-ordering": (
      <PlayerOrdering
        players={players}
        player_groups={playerGroups}
        team_name={teamName}
      />
    ),
    "players-groups": <PlayerGrouping />,
    "health-wellness": <WellnessSurvey />,
    "treatment-log-moved": <MovedToHealth page="Treatment Log" path="/health/treatment-log" />,
    "injuries-moved": <MovedToHealth page="Injuries" path="/health/injuries" verb="have" />,
    "drills-management": <DrillMergeTable />,
    "override-datapoint": <OverrideDatapoint allMetrics={allMetrics} />,
    "delete-data": <DeleteData />,
    "session-management": <SessionManagement />,
    "daily-active-roster": <DailyActiveRoster />,
    "automatic-actions": <AutomaticActions />,
    "flag-reminders": <FlagReminders />,
    "user-defined-metrics": <UserDefinedMetrics />,
    "reports": <ReportBulletPicker />,
    "custom-coloring": <CustomColorStrategies />,
    "platform-permissions": <PlatformPermissions userPermissions={userPermissions}/>,
    "coach-onboarding": <CoachOnboarding />,
    "gps-metric-mapping": <GpsMetricMapping />,
    "third-party-integrations": <ThirdPartyIntegration />,
    "graphics-package": <GraphicsPackageDownloader />,
    "user-defined-flags": <UserDefinedFlags />,
  };

  let titles = {
    "overview": "Overview",
    "metrics": "Metrics",
    "activity-log": "Activity Log",
    "platform-usage": "Platform Usage",
    "gps-metric-mapping": "Configure GPS Imports",
    "baselines-team": "Baselines",
    "custom-charts": "Custom Charts",
    "calendar-games": "Games",
    "calendar-rest": "Calendar Tags",
    "players-roster": "Roster",
    "players-nicknames": "Nicknames",
    "players-ordering": "Ordering",
    "players-groups": "Groups",
    "health-wellness": "Wellness Survey",
    "treatment-log-moved": "Treatment Log",
    "injuries-moved": "Injuries",
    "drills-management": "Management",
    "override-datapoint": "Override Datapoint",
    "delete-data": "Delete Data",
    "session-management": "Session Management",
    "daily-active-roster": "Daily Active Roster",
    "automatic-actions": "Automatic Actions",
    "flag-reminders": "Flag Reminders",
    "user-defined-metrics": "User Defined Metrics",
    "coach-onboarding": "Coach Management",
    "custom-coloring": "Custom Coloring",
    "platform-permissions": "Platform Permissions",
    "third-party-integrations": "External Data Connections",
    "graphics-package": "Graphics Download",
    "user-defined-flags": "User Defined Flags",
    "reports": "Pick Your Bullets",
  };

  const DenseMenuItem = ({ children, ...props }) => (
    <Menu.Item
      {...props}
      style={{ height: 30, lineHeight: "30px", margin: "2px 0px", textAlign: 'left' }}
    >
      {children}
    </Menu.Item>
  );

  const CustomizedMenuItemGroup = ({ title }) => (
    <Menu.ItemGroup
      key="baselines"
      title={<div style={{ fontSize: 12 }}>{title}</div>}
      style={{
        height: 30,
        lineHeight: 30,
        textAlign: 'left'
      }}
    />
  );

  return (
    <>
      <Navbar />
      <HelpButton page="Team Settings" />
      <Row>
        <Col span={20} offset={2}>
          <div
            style={{
              display: "flex",
              gap: 16,
              margin: "24px 16px",
              alignItems: "center",
            }}
          >
            <img
              src={team_logo}
              style={{ height: 48, width: 48, objectFit: "contain" }}
            />
            <Typography.Title level={5} style={{ marginBottom: 0 }}>
              {teamName} <span style={{ color: "gray" }}>({user.email})</span>
            </Typography.Title>
          </div>
        </Col>
      </Row>
      <div>
        <Row justify={"center"} style={{ paddingBottom: 24 }}>
          <Col span={4}>
            <Menu
              selectedKeys={[tab]}
              onSelect={({ key }) => {
                window.scrollTo({ top: 0, behavior: "smooth" });
                navigate(`/settings/${key}`);
              }}
            >
              <CustomizedMenuItemGroup title="General" />
              <DenseMenuItem key="overview" icon={<MdOutlineSettings />}>
                Overview
              </DenseMenuItem>
              <DenseMenuItem key="metrics" icon={<PiListNumbers style={{ marginBottom: 3 }} />}>
                Metrics
              </DenseMenuItem>
              <DenseMenuItem key="baselines-team" 
              icon={userPermissions?.ViewBaselines ? <TeamOutlined /> : <TfiLock/>}>
                Baselines
              </DenseMenuItem>
              <DenseMenuItem key="activity-log" icon={<ImList2 style={{ marginBottom: 3 }} />}>
                Activity Log
              </DenseMenuItem>
              <DenseMenuItem key="platform-usage" icon={<LineChartOutlined style={{ marginBottom: 3 }} />}>
                Platform Usage
              </DenseMenuItem>
              <Menu.Divider style={{ margin: "12px 0px" }} />
              <CustomizedMenuItemGroup title="Calendar" />
              <DenseMenuItem
                key="calendar-games"
                icon={<GiWhistle style={{ marginBottom: 3 }} />}
              >
                Games
              </DenseMenuItem>
              <DenseMenuItem
                key="calendar-rest"
                icon={<IoMdAirplane style={{ marginBottom: 3 }} />}
              >
                Calendar Tags
              </DenseMenuItem>
              <Menu.Divider style={{ margin: "12px 0px" }} />
              <CustomizedMenuItemGroup title="Players" />
              <DenseMenuItem
                key="players-roster"
                icon={userPermissions?.RosterViewPlayers ? <FaRegAddressCard style={{ marginBottom: 3 }} /> : <TfiLock/>}
              >
                Roster
              </DenseMenuItem>
              <DenseMenuItem
                key="players-nicknames"
                icon={<MdDriveFileRenameOutline style={{ marginBottom: 3 }} />}
              >
                Nicknames
              </DenseMenuItem>
              <DenseMenuItem
                key="players-ordering"
                icon={<PiListNumbers style={{ marginBottom: 3 }} />}
              >
                Ordering
              </DenseMenuItem>
              <DenseMenuItem
                key="players-groups"
                icon={<HiOutlineRectangleGroup style={{ marginBottom: 3 }} />}
              >
                Groups
              </DenseMenuItem>

              <Menu.Divider style={{ margin: "12px 0px" }} />
              <CustomizedMenuItemGroup title="Health" />
              <DenseMenuItem
                key="health-wellness"
                icon={<RiSurveyLine style={{ marginBottom: 3 }} />}
              >
                Wellness Survey
              </DenseMenuItem>
              <DenseMenuItem
                key="treatment-log-moved"
                icon={<ImList2 style={{ marginBottom: 3 }} />}
              >
                Treatment Log
              </DenseMenuItem>
              <DenseMenuItem
                key="injuries-moved"
                icon={<MdOutlinePersonalInjury style={{ marginBottom: 3 }} />}
              >
                Injuries
              </DenseMenuItem>
              <Menu.Divider style={{ margin: "12px 0px" }} />
              <CustomizedMenuItemGroup title="Drills" />
              <DenseMenuItem
                key="drills-management"
                icon={<MdOutlineLibraryBooks style={{ marginBottom: 3 }} />}
              >
                Management
              </DenseMenuItem>
              <Menu.Divider style={{ margin: "12px 0px" }} />
              <CustomizedMenuItemGroup title="Manual Changes" />
              <DenseMenuItem
                key="override-datapoint"
                icon={canOverrideDatapoints ? <TbDatabaseCog style={{ marginBottom: 3 }} /> : <TfiLock/>}
              >
                Override Datapoint
              </DenseMenuItem>
              <DenseMenuItem
                key="delete-data"
                icon={canDeleteAnyDatapoint ? <MdDeleteOutline style={{ marginBottom: 3 }} /> : <TfiLock/>}
              >
                Delete Data
              </DenseMenuItem>
              <DenseMenuItem
                key="session-management"
                icon={userPermissions?.ManageGPSTrainingSessions ? <MdDriveFileRenameOutline style={{ marginBottom: 3 }} /> : <TfiLock/>}
              >
                Session Management
              </DenseMenuItem>
              <DenseMenuItem
                key="daily-active-roster"
                icon={userPermissions?.ManageDailyActiveRoster ? <CalendarOutlined /> : <TfiLock/>}
              >
                Daily Active Roster
              </DenseMenuItem>
              <Menu.Divider style={{ margin: "12px 0px" }} />
              <CustomizedMenuItemGroup title="Automatic Changes" />
              <DenseMenuItem
                key="automatic-actions"
                icon={<GrTrigger style={{ marginBottom: 3 }} />}
              >
                Automatic Actions
              </DenseMenuItem>
              <DenseMenuItem
                  key="flag-reminders"
                  icon={<BsFlag style={{ marginBottom: 3 }} />}
                >
                  Flag Reminders
                </DenseMenuItem>
              <Menu.Divider style={{ margin: "12px 0px" }} />
              <CustomizedMenuItemGroup title="Import Data" />

                {
                  isStaff && (
                    <DenseMenuItem key="gps-metric-mapping" icon={<BsClipboard2Data style={{ marginBottom: 3 }} />}>
                    Configure GPS Imports
                  </DenseMenuItem>
                  )
                }

                <DenseMenuItem
                  key="third-party-integrations"
                  icon={third_party_integration_management_enabled ? <ApiOutlined style={{ marginBottom: 3 }} /> :<TfiLock/>}
                >
                  External Data Connections
                </DenseMenuItem>

              <Menu.Divider style={{ margin: "12px 0px" }} />
              <CustomizedMenuItemGroup title="Elite Features" />
                <DenseMenuItem
                  key="user-defined-metrics"
                  icon={user_defined_metrics_enabled ? <MdOutlineCalculate style={{ marginBottom: 3 }} /> : <TfiLock/>}
                >
                  User Defined Metrics
                </DenseMenuItem>
                <DenseMenuItem
                  key="user-defined-flags"
                  icon={user_defined_flags_enabled ? <MdOutlineFlag style={{ marginBottom: 3 }} /> : <TfiLock />}
                >
                  User Defined Flags
                </DenseMenuItem>
                <DenseMenuItem
                  key="reports"
                  icon={bullet_picker_enabled ? <ImList2 style={{ marginBottom: 3 }} /> :<TfiLock/> }
                >
                  Pick Your Bullets
                </DenseMenuItem>
                {isStaff && (
                  <DenseMenuItem key="custom-charts" icon={<IoBarChart />}>
                    Custom Charts
                  </DenseMenuItem>
                )}
                {<DenseMenuItem
                  key="custom-coloring"
                  icon={custom_colors_enabled ? <HighlightOutlined style={{ marginBottom: 3 }} /> :<TfiLock/> }
                >
                  Custom Coloring
                </DenseMenuItem>
                }

                {<DenseMenuItem
                    key="coach-onboarding"
                    icon={add_coach_enabled ? <FaChalkboardTeacher style={{ marginBottom: 3 }} /> : <TfiLock/> }
                  >
                    Coach Management
                  </DenseMenuItem>
                }
                <DenseMenuItem
                  key="platform-permissions"
                  icon={platform_permissions_enabled ? <LockOutlined style={{ marginBottom: 3 }} /> :<TfiLock/>}
                >
                  Platform Permissions
                </DenseMenuItem>
                { isStaff ? (
                  <DenseMenuItem
                  key="graphics-package"
                  icon={<BsDownload style={{ marginBottom: 3 }} />}
                >
                  Graphics Download
                </DenseMenuItem>
                ) : null }

            </Menu>
          </Col>
          <Col span={16} style={{ textAlign: "left", paddingLeft: 48 }}>
            <SettingTitle title={titles[tab]} description={SETTING_DESCRIPTIONS[tab]} />
            {contents[tab]}
          </Col>
        </Row>
      </div>
      {/* <Footer style={{color: 'grey', marginTop:'70px'}}>GPS DataViz 2021</Footer> */}
    </>
  );
};
